import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { DARK_INPUT, MfaField, fmtDate, errMsg } from "./shared";

export default function DirectorCertificateCard({ cert, isSA, onChange }) {
  const [edit, setEdit] = useState(false);
  if (!cert) return <div className="admin-card p-5 text-slate-500 text-sm">Loading…</div>;
  const d = cert.details || {};
  const daysLeft = d.valid_to ? Math.floor((new Date(d.valid_to) - new Date()) / 864e5) : null;
  return (
    <div className="admin-card p-5" data-testid="dsc-cert-card">
      <div className="font-bold flex items-center gap-2 mb-3"><KeyRound size={16} className="text-orange-400"/>Director's DSC certificate</div>
      {cert.registered && !edit ? (
        <div className="text-xs space-y-1.5">
          <Row k="Signatory" v={`${cert.signer_name} · ${cert.designation}`}/>
          <Row k="Certificate name" v={d.subject_cn} testid="dsc-cert-cn"/>
          <Row k="Issued by" v={d.issuer_cn}/>
          <Row k="Serial" v={<span className="font-mono">{d.serial}</span>}/>
          <Row k="Valid" v={`${fmtDate(d.valid_from)} → ${fmtDate(d.valid_to)}`}/>
          <Row k="CA chain certs" v={cert.ca_count ? `${cert.ca_count} uploaded` : "none (optional)"}/>
          {daysLeft !== null && daysLeft < 30 && <div className="flex gap-2 text-amber-300 mt-2" data-testid="dsc-expiry-warning"><AlertTriangle size={14}/>Certificate expires in {daysLeft} day(s). Renew the DSC and re-register.</div>}
          {isSA && <Button size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300 mt-3" onClick={() => setEdit(true)} data-testid="dsc-replace-btn">Replace certificate</Button>}
        </div>
      ) : isSA ? <CertForm onDone={() => { setEdit(false); onChange(); }} onCancel={cert.registered ? () => setEdit(false) : null}/>
        : <p className="text-xs text-slate-500">Not registered yet. A Super Admin must register the Director's public certificate.</p>}
    </div>
  );
}

const Row = ({ k, v, testid }) => <div className="grid grid-cols-[110px_1fr] gap-2"><span className="text-slate-500">{k}</span><span className="text-slate-200 break-words" data-testid={testid}>{v}</span></div>;

function CertForm({ onDone, onCancel }) {
  const [file, setFile] = useState(null);
  const [ca, setCa] = useState(null);
  const [name, setName] = useState("");
  const [mfa, setMfa] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); if (ca) fd.append("ca_file", ca);
      fd.append("signer_name", name); fd.append("designation", "Director"); fd.append("mfa_code", mfa);
      await api.post("/admin/dsc/certificate", fd);
      toast.success("Director certificate registered"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const fileCls = "block w-full text-xs text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-slate-200 mt-1";
  return (
    <div className="space-y-3 text-xs">
      <p className="text-slate-500">Upload only the <b>public</b> certificate (.cer) exported from the token — never a .pfx, private key or PIN.</p>
      <div><label className="font-semibold text-slate-400">Director's full name</label><Input value={name} onChange={(e) => setName(e.target.value)} className={`${DARK_INPUT} mt-1`} data-testid="dsc-signer-name"/></div>
      <div><label className="font-semibold text-slate-400">Public certificate (.cer / .pem)</label><input type="file" accept=".cer,.crt,.pem,.der" onChange={(e) => setFile(e.target.files[0])} className={fileCls} data-testid="dsc-cert-file"/></div>
      <div><label className="font-semibold text-slate-400">CA chain certificates (optional)</label><input type="file" accept=".cer,.crt,.pem,.p7b,.p7c" onChange={(e) => setCa(e.target.files[0])} className={fileCls} data-testid="dsc-ca-file"/></div>
      <MfaField value={mfa} onChange={setMfa} testid="dsc-cert-mfa"/>
      <div className="flex gap-2">
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={!file || !name.trim() || mfa.length !== 6 || busy} onClick={save} data-testid="dsc-cert-save">Register certificate</Button>
        {onCancel && <Button size="sm" variant="ghost" className="text-slate-400" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}
